import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
} from "react";
import UIkit from "uikit";
import { Flex } from "uikit-react";
import { useCookies } from "react-cookie";

import RecordsPerPage from "../../../common/RecordsPerPage";
import Pagination from "../../../common/Pagination";
import StorageItemAdd from "./add";

const DEFAULT_RPP = 10;

const ITEM_CATEGORY_OPTIONS = [
  { label: "All Categories", value: "" },
  { label: "Bed", value: "bed" },
  { label: "Bath", value: "bath" },
  { label: "Kitchen", value: "kitchen" },
  { label: "Cleaning", value: "cleaning" },
  { label: "Paper Goods", value: "paper_goods" },
  { label: "Maintenance", value: "maintenance" },
  { label: "Other", value: "other" },
];

const STATUS_FILTER_OPTIONS = [
  { label: "All", value: "" },
  { label: "Active", value: "active" },
  { label: "Inactive", value: "inactive" },
];

const UNIT_OPTIONS = [
  { label: "Each", value: "each" },
  { label: "Pack", value: "pack" },
  { label: "Box", value: "box" },
  { label: "Case", value: "case" },
];

/** Seed data for now – replace with API later */
const ITEMS_DUMMY = [
  {
    id: "item-1",
    name: "Queen Sheet Set – White",
    sku: "QS-WHT",
    category: "bed",
    unit: "each",
    reorder_point: 10,
    notes: "Standard for all queen beds.",
    active: true,
  },
  {
    id: "item-2",
    name: "Bath Towel – White",
    sku: "BT-WHT",
    category: "bath",
    unit: "each",
    reorder_point: 30,
    notes: "One per guest + 2 extras per home.",
    active: true,
  },
  {
    id: "item-3",
    name: "Dish Sponge – 3 Pack",
    sku: "SPG-3PK",
    category: "kitchen",
    unit: "pack",
    reorder_point: 15,
    notes: "1 new sponge per turnover.",
    active: true,
  },
  {
    id: "item-4",
    name: "Toilet Paper – 12 Pack",
    sku: "TP-12",
    category: "paper_goods",
    unit: "pack",
    reorder_point: 20,
    notes: "",
    active: false,
  },
];

const categoryLabel = (value) =>
  (ITEM_CATEGORY_OPTIONS.find((c) => c.value === value) || {}).label ||
  value ||
  "—";

const unitLabel = (value) =>
  (UNIT_OPTIONS.find((u) => u.value === value) || {}).label || value || "—";

export default function StorageItemsIndex() {
  const [cookies] = useCookies(["records-per-page"]);
  const recordsPerPage =
    Number(cookies["records-per-page"] || DEFAULT_RPP) || DEFAULT_RPP;

  const [items, setItems] = useState(ITEMS_DUMMY);
  const [editingItem, setEditingItem] = useState(null);

  const [criteria, setCriteria] = useState({
    search: "",
    category: "",
    status: "",
    page: 0,
  });

  useEffect(() => {
    document.title = "Storage Items - RueBaRue";
  }, []);

  const handleCriteriaChange = useCallback((key, value) => {
    setCriteria((prev) => ({
      ...prev,
      [key]: value,
      page:
        key === "search" || key === "category" || key === "status"
          ? 0
          : prev.page,
    }));
  }, []);

  const handleIndexChanged = useCallback((newPageIndex) => {
    setCriteria((prev) => ({ ...prev, page: newPageIndex }));
  }, []);

  const toggleActive = useCallback((itemId) => {
    setItems((prev) =>
      prev.map((it) =>
        it.id === itemId ? { ...it, active: !it.active } : it
      )
    );
  }, []);

  const filtered = useMemo(() => {
    return items.filter((it) => {
      if (criteria.category && it.category !== criteria.category) {
        return false;
      }

      if (criteria.status === "active" && !it.active) return false;
      if (criteria.status === "inactive" && it.active) return false;

      if (criteria.search) {
        const q = criteria.search.toLowerCase();
        const hay = `${it.name || ""} ${it.sku || ""}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }

      return true;
    });
  }, [items, criteria]);

  const totalPages = Math.max(
    1,
    Math.ceil(filtered.length / recordsPerPage)
  );
  const pageItems = filtered.slice(
    criteria.page * recordsPerPage,
    criteria.page * recordsPerPage + recordsPerPage
  );

  const openAdd = () => {
    setEditingItem(null);
    UIkit.offcanvas("#modal-storage-item-add").show();
  };

  const openEdit = (item) => {
    setEditingItem(item);
    UIkit.offcanvas("#modal-storage-item-add").show();
  };

  const handleSaveItem = async (payload) => {
    // local-only upsert
    setItems((prev) => {
      if (payload.id) {
        // update
        return prev.map((it) => (it.id === payload.id ? payload : it));
      }
      // create
      const id = `item-${Date.now()}`;
      return [...prev, { ...payload, id }];
    });

    UIkit.notification("Item saved", "success");
    return true; // tells modal to close
  };

  const handleCancelItem = () => {
    setEditingItem(null);
  };

  return (
    <>
      <section>
        <div id="hms-page-title">
          <h1 className="uk-heading-small">Storage Items</h1>
        </div>

        <div id="hms-main-body">
          <div className="uk-flex uk-flex-between uk-flex-top">
            <button
              className="uk-button hms-btn hms-red-btn"
              type="button"
              onClick={openAdd}
            >
              <span uk-icon="icon: plus; ratio: 0.7"></span> Item
            </button>
          </div>

          <div className="uk-card uk-card-small uk-card-default hms-form-card card-without-filters">
            <div className="uk-overflow-auto">
              {/* Filters */}
              <Flex
                alignment="between top"
                className="guest-form-top-filters"
              >
                <form
                  className="uk-form uk-flex uk-flex-middle hms-form"
                  onSubmit={(e) => e.preventDefault()}
                >
                  {/* Search */}
                  <div className="uk-inline uk-margin-small-right">
                    <div className="uk-search uk-search-default uk-form-custom">
                      {criteria.search ? (
                        <a
                          className="uk-form-icon-flip uk-form-icon"
                          uk-icon="icon: close"
                          onClick={() =>
                            handleCriteriaChange("search", "")
                          }
                        ></a>
                      ) : (
                        <span
                          className="uk-search-icon-flip"
                          uk-search-icon="true"
                        />
                      )}
                      <input
                        className="uk-search-input"
                        type="search"
                        placeholder="Search item or SKU"
                        value={criteria.search}
                        onChange={(e) =>
                          handleCriteriaChange("search", e.target.value)
                        }
                      />
                    </div>
                  </div>

                  {/* Category filter */}
                  <div
                    className="uk-inline uk-margin-small-right"
                    uk-form-custom="target: > * > span:first-child"
                  >
                    <select
                      value={criteria.category}
                      onChange={(e) =>
                        handleCriteriaChange("category", e.target.value)
                      }
                    >
                      {ITEM_CATEGORY_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                    <button
                      className="uk-button uk-button-default"
                      type="button"
                      tabIndex="-1"
                    >
                      <span className="select-value">
                        {criteria.category
                          ? categoryLabel(criteria.category)
                          : "All Categories"}
                      </span>
                      <span
                        uk-icon="icon: chevron-down"
                        className="hms-align-right"
                      ></span>
                    </button>
                  </div>

                  {/* Status filter */}
                  <div
                    className="uk-inline uk-margin-small-right"
                    uk-form-custom="target: > * > span:first-child"
                  >
                    <select
                      value={criteria.status}
                      onChange={(e) =>
                        handleCriteriaChange("status", e.target.value)
                      }
                    >
                      {STATUS_FILTER_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                    <button
                      className="uk-button uk-button-default"
                      type="button"
                      tabIndex="-1"
                    >
                      <span className="select-value">
                        {criteria.status
                          ? STATUS_FILTER_OPTIONS.find(
                              (o) => o.value === criteria.status
                            )?.label
                          : "All Statuses"}
                      </span>
                      <span
                        uk-icon="icon: chevron-down"
                        className="hms-align-right"
                      ></span>
                    </button>
                  </div>
                </form>
              </Flex>

              {/* Table */}
              <table className="uk-table uk-table-hover uk-table-divider hms-table">
                <thead>
                  <tr>
                    <th>Active</th>
                    <th className="uk-table-expand">Item</th>
                    <th>SKU</th>
                    <th>Category</th>
                    <th>Unit</th>
                    <th>Reorder Point</th>
                    <th>Notes</th>
                    <th className="actions">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {pageItems.length === 0 && (
                    <tr>
                      <td colSpan={8}>
                        <div className="uk-text-center uk-padding-small uk-text-meta">
                          No items found. Adjust your filters.
                        </div>
                      </td>
                    </tr>
                  )}

                  {pageItems.map((it) => (
                    <tr key={it.id}>
                      <td>
                        <div className="autosend-checkbox">
                          <label className="setting-switch">
                            <input
                              type="checkbox"
                              checked={!!it.active}
                              onChange={() => toggleActive(it.id)}
                            />
                            <span className="setting-slider setting-round"></span>
                          </label>
                        </div>
                      </td>

                      <td className="semi-bold-font">{it.name}</td>
                      <td>{it.sku || "—"}</td>
                      <td>{categoryLabel(it.category)}</td>
                      <td>{unitLabel(it.unit)}</td>
                      <td>{it.reorder_point ?? "—"}</td>
                      <td>{it.notes || "—"}</td>

                      <td className="hms-action-btn">
                        <div className="uk-inline">
                          <button
                            className="uk-button"
                            type="button"
                            onClick={() => openEdit(it)}
                            uk-tooltip="Edit"
                          >
                            <img src="/images/edit.svg" alt="Edit" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              <Flex
                alignment="between top"
                className="guest-form-top-filters"
              >
                <Pagination
                  count={totalPages}
                  index={Math.min(criteria.page, totalPages - 1)}
                  onIndexChanged={handleIndexChanged}
                />
                <RecordsPerPage />
              </Flex>
            </div>
          </div>
        </div>
      </section>

      {/* Add/Edit modal */}
      <StorageItemAdd
        item={editingItem}
        flagIsNew={!editingItem}
        flagIsProcessing={false}
        onSave={handleSaveItem}
        onCancel={handleCancelItem}
      />
    </>
  );
}